import React, { useCallback, useEffect, useState } from 'react';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey, useIssueId } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useMediaContext } from '@atlassian/jira-issue-media-context-service/src/main.tsx';
import { proformaFormEditModeChange } from '@atlassian/jira-issue-view-store/src/actions/proforma-actions.tsx';
import { fetchViewContextRequest } from '@atlassian/jira-issue-view-store/src/common/media/view-context/view-context-actions.tsx';
import { toProformaEnvironment } from '@atlassian/jira-proforma-utils/src/common/utils/to-proforma-environment/index.tsx';
import { getUrls, ANALYTICS_CHANNEL } from '@atlassian/jira-proforma-utils/src/index.tsx';
import {
	useProjectType,
	useProjectId,
	useProjectKey,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import type { BackendSettings } from '@atlassian/proforma-common-core/jira-common-models';
import { IssueViewApp, type IssueViewSettings } from './issue-view-forms/index.tsx';
import { useFetchProjectToken } from './utils.tsx';

type Props = {
	isProjectAdmin: boolean;
	canEditIssues: boolean;
	panelAddFormButtonClickedCount: number;
	useEditModeCallback: (isFormInEditMode: boolean) => void;
	getMediaReadToken: () => void;
};

export const ProformaFormsView = ({
	canEditIssues,
	isProjectAdmin,
	panelAddFormButtonClickedCount,
	useEditModeCallback,
	getMediaReadToken,
}: Props) => {
	const { locale } = useIntl();
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const environment = useEnvironment();
	const issueId = useIssueId();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectId = useProjectId(projectKey);
	const projectType = useProjectType(projectKey);
	const [settings, setSettings] = useState<BackendSettings<IssueViewSettings>>();
	const mediaContextDetails = useMediaContext(issueKey);
	const { data: projectUploadToken } = useFetchProjectToken(projectId, mediaContextDetails);
	const mediaApiContextDetails =
		mediaContextDetails?.viewContext && projectUploadToken
			? {
					uploadContext: projectUploadToken,
					viewContext: mediaContextDetails.viewContext,
				}
			: undefined;

	useEffect(() => {
		if (issueId && projectId && projectType && accountId) {
			setSettings({
				analytics: { channel: ANALYTICS_CHANNEL },
				flags: {},
				urls: getUrls(environment, cloudId),
				environment: toProformaEnvironment(environment),
				context: {
					accountId,
					issueKey,
					issueId: Number(issueId),
					projectId: Number(projectId),
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					projectType: projectType as 'business' | 'service_desk' | 'software',
					userIsProjectAdmin: isProjectAdmin,
					userCanEditIssue: canEditIssues,
					showFormVisibility: projectType === SERVICE_DESK_PROJECT,
					intlLocale: locale,
					editModeCallback: useEditModeCallback,
				},
			});
		}
	}, [
		accountId,
		environment,
		cloudId,
		issueKey,
		issueId,
		projectId,
		projectType,
		isProjectAdmin,
		canEditIssues,
		locale,
		useEditModeCallback,
	]);

	const mediaReadTokenRefreshCallback = useCallback(() => getMediaReadToken(), [getMediaReadToken]);

	return projectId !== undefined && projectId !== '' && settings ? (
		<IssueViewApp
			settings={settings}
			addFormButtonClickedCount={panelAddFormButtonClickedCount}
			mediaContextDetails={mediaApiContextDetails}
			mediaReadTokenRefresh={mediaReadTokenRefreshCallback}
		/>
	) : null;
};

const mapDispatchToProps = {
	useEditModeCallback: proformaFormEditModeChange,
	getMediaReadToken: fetchViewContextRequest,
};

export const ConnectedProformaFormsView = connect(null, mapDispatchToProps)(ProformaFormsView);
